import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import API_BASE_URL from '@/config';
import {
  playNotificationSound,
  isAudioMuted,
  toggleAudioMute as toggleMuteLib,
  initAudioUnlock,
} from '@/lib/audioAlert';
import { registerServiceWorker } from '@/lib/pushSubscription';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, token } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [audioMuted, setAudioMuted] = useState(() => isAudioMuted());

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const backoffRef = useRef(1000);
  const listenersRef = useRef(new Set());
  const isMountedRef = useRef(true);

  // Initialize Web Audio user-activation unlock once on mount
  useEffect(() => {
    isMountedRef.current = true;
    initAudioUnlock();
    registerServiceWorker();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const toggleAudio = useCallback(() => {
    const next = toggleMuteLib();
    setAudioMuted(next);
    return next;
  }, []);

  // Authoritative fetch from MySQL
  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/api/notifications?limit=30');
      if (res.data) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(typeof res.data.unread_count === 'number' ? res.data.unread_count : 0);
      }
    } catch (err) {
      console.warn('[Notifications] Failed to fetch notifications:', err);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));

      await api.patch(`/api/notifications/${notificationId}/read`);
    } catch (err) {
      console.warn('[Notifications] Failed to mark as read:', err);
      // Revert/refresh on error
      refreshNotifications();
    }
  }, [refreshNotifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      await api.post('/api/notifications/read-all');
    } catch (err) {
      console.warn('[Notifications] Failed to mark all as read:', err);
      refreshNotifications();
    }
  }, [refreshNotifications]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      let wasUnread = false;
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === notificationId);
        if (target && !target.is_read) {
          wasUnread = true;
        }
        return prev.filter((n) => n.id !== notificationId);
      });
      if (wasUnread) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }

      const res = await api.delete(`/api/notifications/${notificationId}`);
      if (res.data && typeof res.data.unread_count === 'number') {
        setUnreadCount(res.data.unread_count);
      }
    } catch (err) {
      console.warn('[Notifications] Failed to delete notification:', err);
      refreshNotifications();
    }
  }, [refreshNotifications]);

  const clearAllNotifications = useCallback(async () => {
    try {
      setNotifications([]);
      setUnreadCount(0);
      await api.delete('/api/notifications');
    } catch (err) {
      console.warn('[Notifications] Failed to clear all notifications:', err);
      refreshNotifications();
    }
  }, [refreshNotifications]);

  // Listener dispatch for custom subscription hooks
  const dispatchEvent = useCallback((eventData) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(eventData);
      } catch (e) {
        console.error('[Notifications] Error in event listener callback:', e);
      }
    });
  }, []);

  // SSE Connect Loop
  const connectSSE = useCallback(async () => {
    if (!isAuthenticated || !token) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      // 1. Fetch single-use 60s connection ticket
      const ticketResp = await api.post('/api/realtime/ticket');
      const ticket = ticketResp.data?.ticket;
      if (!ticket) {
        throw new Error('No ticket returned by realtime manager');
      }

      // 2. Establish EventSource connection
      const sseUrl = `${API_BASE_URL}/api/realtime/stream?ticket=${encodeURIComponent(ticket)}`;
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      es.onopen = () => {
        if (!isMountedRef.current) return;
        setIsRealtimeConnected(true);
        backoffRef.current = 1000; // Reset exponential backoff
        // Reconcile state with authoritative MySQL on successful (re)connection
        refreshNotifications();
      };

      es.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connected') {
            return;
          }

          // Dispatch event to page-level realtime hooks
          dispatchEvent(data);

          // Handle notifications contract: deduplicate by notification_id
          if (data.notification_id) {
            setNotifications((prev) => {
              if (prev.some((n) => n.id === data.notification_id)) {
                return prev; // Duplicate protection
              }
              // Immediately fetch fresh list from authoritative MySQL
              refreshNotifications();
              return prev;
            });

            // Active tab audio alert: user only gets sound when assigned something
            if (!isAudioMuted() && data.type === 'prescription.assigned') {
              playNotificationSound();
            }
          }
        } catch (e) {
          console.debug('[SSE] Non-JSON or keep-alive message received', e);
        }
      };

      es.onerror = () => {
        if (!isMountedRef.current) return;
        setIsRealtimeConnected(false);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Exponential backoff reconnect: 1s -> 2s -> 4s -> max 30s
        const delay = backoffRef.current;
        backoffRef.current = Math.min(30000, delay * 2);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && isAuthenticated) {
            connectSSE();
          }
        }, delay);
      };
    } catch (err) {
      console.warn('[SSE] Ticket acquisition error, retrying in 3s:', err);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && isAuthenticated) {
          connectSSE();
        }
      }, 3000);
    }
  }, [isAuthenticated, token, dispatchEvent, refreshNotifications]);

  // Manage connection based on authentication state
  useEffect(() => {
    if (isAuthenticated) {
      refreshNotifications();
      connectSSE();
    } else {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      setIsRealtimeConnected(false);
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, connectSSE, refreshNotifications]);

  // Hook for registering realtime event listeners
  const subscribeListener = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const value = {
    notifications,
    unreadCount,
    isRealtimeConnected,
    audioMuted,
    toggleAudio,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refreshNotifications,
    subscribeListener,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Hook to listen for specific real-time event types and trigger a callback (e.g. refetch table data).
 * @param {Array<string>|string} eventTypes - Event type(s) to listen for, or '*' for all.
 * @param {Function} callback - Callback function receiving eventData.
 */
export const useRealtimeEvent = (eventTypes, callback) => {
  const { subscribeListener } = useNotifications();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];

    const handler = (eventData) => {
      if (types.includes('*') || types.includes(eventData.type)) {
        if (callbackRef.current) {
          callbackRef.current(eventData);
        }
      }
    };

    return subscribeListener(handler);
  }, [eventTypes, subscribeListener]);
};
